import { z } from 'zod';
import { idSchema, isoDateTimeSchema } from './primitives';
/**
 * OCR ciblé et multi-zones (cahier des charges §6 et §20.1).
 *
 * Le moteur ne lit jamais le reçu entier : il lit des **zones** déclarées en
 * base et modifiables depuis l'administration, sans redéploiement. Les
 * coordonnées sont relatives (0 → 1) pour rester valides quels que soient la
 * résolution de numérisation et le format papier.
 */
export const OCR_FIELD_KEYS = {
    ORDER_NUMBER: 'order_number',
    SUB_ORDER_NUMBER: 'sub_order_number',
    DELIVERY_DATE: 'delivery_date',
    ISSUE_DATE: 'issue_date',
    CUSTOMER_NAME: 'customer_name',
    BRANCH_CODE: 'branch_code',
    DRIVER_NAME: 'driver_name',
    DRIVER_PLATE: 'driver_plate',
    RECEIPT_REFERENCE: 'receipt_reference',
    TOTAL_AMOUNT: 'total_amount',
    CURRENCY: 'currency',
    ITEM_COUNT: 'item_count',
    ITEMS: 'items',
    SIGNATURE: 'signature',
    BARCODE: 'barcode',
    QR_CODE: 'qr_code',
};
export const OCR_FIELD_LABELS = {
    order_number: 'Numéro de commande',
    sub_order_number: 'Sous-numéro',
    delivery_date: 'Date de livraison',
    issue_date: "Date d'émission",
    customer_name: 'Client',
    branch_code: 'Bureau',
    driver_name: 'Chauffeur',
    driver_plate: 'Plaque',
    receipt_reference: 'Référence du reçu',
    total_amount: 'Montant total',
    currency: 'Devise',
    item_count: 'Nombre de produits',
    items: 'Liste des produits',
    signature: 'Signature',
    barcode: 'Code-barres',
    qr_code: 'QR Code',
};
/** Nature de la donnée attendue : pilote la normalisation et la validation. */
export const OCR_ZONE_MODES = {
    TEXT: 'TEXT',
    NUMBER: 'NUMBER',
    DATE: 'DATE',
    AMOUNT: 'AMOUNT',
    BARCODE: 'BARCODE',
    QR_CODE: 'QR_CODE',
    SIGNATURE: 'SIGNATURE',
};
export const ocrZoneModeSchema = z.enum([
    OCR_ZONE_MODES.TEXT,
    OCR_ZONE_MODES.NUMBER,
    OCR_ZONE_MODES.DATE,
    OCR_ZONE_MODES.AMOUNT,
    OCR_ZONE_MODES.BARCODE,
    OCR_ZONE_MODES.QR_CODE,
    OCR_ZONE_MODES.SIGNATURE,
]);
/** Rectangle relatif à la page : `x`,`y`,`width`,`height` ∈ [0,1]. */
export const ocrRectSchema = z.object({
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    width: z.number().min(0.001).max(1),
    height: z.number().min(0.001).max(1),
});
/**
 * Ordre des composantes d'une date imprimée.
 *
 * `AUTO` tranche sur la valeur lue quand elle est décisive — `24/07` ne peut
 * être qu'un jour suivi d'un mois — et retombe sur le jour d'abord sinon.
 * Le réglage explicite existe parce que certains émetteurs impriment à
 * l'américaine : sur un reçu daté `07/08/2026`, seule la connaissance du
 * format permet de savoir s'il s'agit du 7 août ou du 8 juillet.
 */
export const dateOrderSchema = z.enum(['AUTO', 'DMY', 'MDY']);
export const DATE_ORDER_LABELS = {
    AUTO: 'Détection automatique',
    DMY: 'Jour / Mois / Année',
    MDY: 'Mois / Jour / Année',
};
export const ocrZoneSchema = z.object({
    fieldKey: z.string().trim().min(2).max(48).regex(/^[a-z][a-z0-9_]*$/, 'Clé technique invalide'),
    label: z.string().trim().min(1).max(80),
    mode: ocrZoneModeSchema.default('TEXT'),
    pageIndex: z.number().int().min(0).default(0),
    rect: ocrRectSchema,
    /**
     * Étiquette imprimée qui précède la valeur, par exemple `Order No:`.
     *
     * Renseignée, elle prend le pas sur le rectangle : la valeur est cherchée
     * après l'étiquette dans le texte du document, sur la même ligne ou sur la
     * suivante. C'est indispensable dès que la mise en page bouge — un champ
     * absent décale tout ce qui suit, et un rectangle fixe viserait alors la
     * mauvaise ligne. Le rectangle reste utilisé comme repère d'affichage et
     * comme repli lorsque l'étiquette n'est pas retrouvée.
     */
    anchor: z.string().trim().max(60).nullable().default(null),
    /** Ordre des composantes, pour les zones de type `DATE`. */
    dateOrder: dateOrderSchema.default('AUTO'),
    /**
     * Autorise la valeur à se poursuivre sur la ligne suivante.
     *
     * Nécessaire pour les raisons sociales, qui débordent régulièrement — « SIMPLY
     * ABNORMAL AND / PROJECTS (PTY) LTD ». Réservé aux champs où la continuation
     * a un sens : l'activer sur un numéro y accolerait la ligne d'après.
     */
    multiline: z.boolean().default(false),
    /** Langues Tesseract, ex. `['fra', 'eng']`. */
    languages: z.array(z.string().min(2).max(8)).min(1).default(['fra', 'eng']),
    /** Restreint l'alphabet reconnu : décisif sur la précision d'un numéro. */
    characterWhitelist: z.string().max(120).nullable().default(null),
    /** Motif de validation appliqué après normalisation. */
    pattern: z.string().max(200).nullable().default(null),
    required: z.boolean().default(false),
    /** En deçà de ce seuil, le reçu part en validation manuelle. */
    minConfidence: z.number().min(0).max(1).default(0.7),
    order: z.number().int().min(0).default(0),
});
export const ocrTemplateSchema = z.object({
    id: idSchema,
    name: z.string(),
    description: z.string().nullable(),
    /** `null` = modèle global, appliqué à tout bureau sans modèle dédié. */
    branchId: idSchema.nullable(),
    branchName: z.string().nullable(),
    version: z.number().int().positive(),
    isActive: z.boolean(),
    zones: z.array(ocrZoneSchema),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const upsertOcrTemplateSchema = z.object({
    name: z.string().trim().min(2).max(120),
    description: z.string().trim().max(400).nullable().optional(),
    branchId: idSchema.nullable().optional(),
    isActive: z.boolean().default(true),
    zones: z.array(ocrZoneSchema).min(1, 'Au moins une zone est requise'),
});
/* ------------------------------------------------------------------------ */
/* Résultats                                                                 */
/* ------------------------------------------------------------------------ */
export const OCR_RESULT_STATUSES = {
    SUCCESS: 'SUCCESS',
    PARTIAL: 'PARTIAL',
    FAILED: 'FAILED',
};
export const ocrResultStatusSchema = z.enum([
    OCR_RESULT_STATUSES.SUCCESS,
    OCR_RESULT_STATUSES.PARTIAL,
    OCR_RESULT_STATUSES.FAILED,
]);
export const OCR_VALUE_SOURCES = {
    OCR: 'OCR',
    BARCODE: 'BARCODE',
    QR_CODE: 'QR_CODE',
    HEURISTIC: 'HEURISTIC',
    MANUAL: 'MANUAL',
};
export const ocrValueSourceSchema = z.enum([
    OCR_VALUE_SOURCES.OCR,
    OCR_VALUE_SOURCES.BARCODE,
    OCR_VALUE_SOURCES.QR_CODE,
    OCR_VALUE_SOURCES.HEURISTIC,
    OCR_VALUE_SOURCES.MANUAL,
]);
export const ocrFieldResultSchema = z.object({
    fieldKey: z.string(),
    label: z.string(),
    rawValue: z.string().nullable(),
    normalizedValue: z.string().nullable(),
    confidence: z.number().min(0).max(1),
    source: ocrValueSourceSchema,
    pageIndex: z.number().int().nonnegative(),
    rect: ocrRectSchema.nullable(),
    accepted: z.boolean(),
    /**
     * Conformité au motif attendu par la zone.
     *
     * `false` désigne une valeur certainement fausse — un sous-numéro qui ne
     * commence pas par `T`, une date impossible. C'est le signal que l'opérateur
     * doit voir avant de valider un lot : un pourcentage global lui dit qu'un
     * doute existe, celui-ci lui dit **où**.
     *
     * `null` : la zone n'impose aucun format, il n'y a rien à vérifier.
     */
    patternMatched: z.boolean().nullable(),
    correctedValue: z.string().nullable(),
    correctedByName: z.string().nullable(),
});
export const ocrResultSchema = z.object({
    id: idSchema,
    receiptId: idSchema,
    status: ocrResultStatusSchema,
    engine: z.string(),
    engineVersion: z.string(),
    templateId: idSchema.nullable(),
    templateName: z.string().nullable(),
    overallConfidence: z.number().min(0).max(1),
    durationMs: z.number().int().nonnegative(),
    attempt: z.number().int().positive(),
    errorMessage: z.string().nullable(),
    fields: z.array(ocrFieldResultSchema),
    createdAt: isoDateTimeSchema,
});
/** Élément de la file de validation manuelle (§4 « Validation manuelle des exceptions »). */
export const reviewItemSchema = z.object({
    receiptId: idSchema,
    lotId: idSchema,
    lotReference: z.string(),
    branchId: idSchema,
    branchCode: z.string(),
    pageCount: z.number().int().positive(),
    reason: z.enum(['OCR_FAILED', 'LOW_CONFIDENCE', 'MISSING_REQUIRED_FIELD', 'DUPLICATE']),
    reasonLabel: z.string(),
    suggestedOrderNumber: z.string().nullable(),
    suggestedSubNumber: z.string().nullable(),
    overallConfidence: z.number().min(0).max(1).nullable(),
    fields: z.array(ocrFieldResultSchema),
    createdAt: isoDateTimeSchema,
});
export const REVIEW_REASON_LABELS = {
    OCR_FAILED: 'OCR en échec',
    LOW_CONFIDENCE: 'Confiance insuffisante',
    MISSING_REQUIRED_FIELD: 'Champ obligatoire manquant',
    DUPLICATE: 'Doublon suspecté',
};
//# sourceMappingURL=ocr.js.map